import ms from 'ms';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type ExportParameters,
  type ExportType,
  useMutationCreateExportJob,
  useQueryExportResult,
} from '~/data/dataExport';

import ToastService from '~/services/toast.service';

import { downloadFile, isZipSignature } from '@vestigas/utils';

type UseDataExportOptions = {
  name: {
    prefix: string;
    category: string;
  };
  parameters: Omit<ExportParameters, 'filename'>;
  exportType: ExportType;
};

function buildExportFilename(
  name: UseDataExportOptions['name'],
  format: ExportParameters['format'],
) {
  const timestamp = new Date()
    .toISOString()
    .slice(0, 19)
    .replace('T', '_')
    .replaceAll(':', '-');

  return `${name.prefix}_${name.category}_${timestamp}.${format}`;
}

function replaceExtension(filename: string, extension: string) {
  return filename.replace(/\.[^.]+$/, `.${extension}`);
}

/**
 * Formats the backend silently bundles into a ZIP when more than one invoice is
 * exported. For these we must sniff the real file type and correct the
 * extension; all other formats download directly.
 */
const BUNDLEABLE_FORMATS = new Set<ExportParameters['format']>(['pdf', 'eml']);

/**
 * Downloads a completed export result. For `pdf`/`eml` exports we fetch the file once,
 * sniff its real type from the response bytes, and correct the extension before
 * saving (reusing the fetched bytes so there is no extra request). All other
 * formats keep the existing direct download behavior.
 */
async function downloadExportResult(
  sasUrl: string,
  filename: string,
  format: ExportParameters['format'] | undefined,
) {
  if (!format || !BUNDLEABLE_FORMATS.has(format)) {
    await downloadFile(sasUrl, filename);

    return;
  }

  try {
    const response = await fetch(sasUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch export: ${response.status}`);
    }

    const buffer = await response.arrayBuffer();
    const extension = isZipSignature(new Uint8Array(buffer)) ? 'zip' : format;

    await downloadFile(
      new Blob([buffer]),
      replaceExtension(filename, extension),
    );
  } catch {
    await downloadFile(sasUrl, filename);
  }
}

/**
 * Custom hook for handling data export functionality.
 * Manages the export job creation, polling for completion, and file download.
 */
export function useDataExport() {
  const { t } = useTranslation('common');

  const {
    mutateAsync,
    data,
    isPending: isPendingCreateExportJob,
    reset: resetMutation,
    variables: mutationVariables,
  } = useMutationCreateExportJob({
    onError: () => {
      ToastService.error(t(($) => $.export.error));
    },
  });

  // Poll for export result
  const {
    data: exportResult,
    isError: isResultError,
    isFetching,
    isFetched,
  } = useQueryExportResult(data?.jobId ?? '', data?.requestId ?? '', {
    enabled: !!data,
    refetchInterval(query) {
      if (['completed', 'failed'].includes(query.state.data?.status ?? '')) {
        return false;
      }

      return ms('2s');
    },
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  // Handle export result changes (download / error toast).
  useEffect(() => {
    if (!isFetched) {
      return;
    }

    if (exportResult?.status === 'completed' && exportResult.sasUrl) {
      const filename = mutationVariables?.parameters?.filename ?? '';
      const format = mutationVariables?.parameters?.format;

      void downloadExportResult(exportResult.sasUrl, filename, format);

      const skippedEntityCount = exportResult.skippedEntityIds.length;

      if (
        mutationVariables?.exportType === 'invoices' &&
        skippedEntityCount > 0
      ) {
        ToastService.success(
          t(($) => $.export.successWithSkippedInvoices, {
            count: skippedEntityCount,
          }),
        );
      } else {
        ToastService.success(t(($) => $.export.success));
      }

      return;
    }

    if (isResultError || exportResult?.status === 'failed') {
      ToastService.error(t(($) => $.export.error));
    }
  }, [exportResult, isResultError, isFetched, mutationVariables, t]);

  // Start export job
  async function handleExport({
    name,
    exportType,
    parameters,
  }: UseDataExportOptions) {
    const filename = buildExportFilename(name, parameters.format);

    resetMutation();
    await mutateAsync({
      exportType,
      parameters: {
        filename,
        ...parameters,
      },
    });
  }

  return {
    handleExport,
    isLoading:
      isPendingCreateExportJob ||
      isFetching ||
      exportResult?.status === 'in_progress',
  };
}
